<template>
    <template v-if="!$settings.isQuote">
        <!-- Pagine login e pubbliche senza layout -->
        <template v-if="isLayoutlessRoute">
            <router-view />
        </template>

        <!-- Pagine autenticate con AdminLayout -->
        <template v-else-if="$cookies.get('admin_token')">
            <AdminLayout :key="adminLayoutKey">
                <router-view />
            </AdminLayout>
        </template>
    </template>

    <FlashMessages
        :bottom="(($settings.isQuote || $settings.isFront) && $settings.isLoggedIn)?'60px':'0px'"
        v-model="flashMessages"
    />

    <LoadingOverlay v-if="overlayVisibility"></LoadingOverlay>

    <template v-if="$settings.isQuote && $settings.isLoggedIn">
        <QuoteBar></QuoteBar>
    </template>

</template>

<script setup>
import { useLoadingOverlayStore } from '@/composables/useLoadingOverlayStore.js'
import { useFlashMessagesStore } from '@jinkosoft/flash-messages'
import { storeToRefs } from 'pinia';

import { useRouter, useRoute } from 'vue-router';

import AdminLayout from '@/layouts/AdminLayout.vue'
import QuoteBar from "@/components/front/quote/QuoteBar.vue";
import {useGlobals, useMutation} from "@/composables/useGlobals.js";
import {computed, inject, onMounted} from "vue";
import VueCookies from "vue-cookies";

const {
    gql,
} = useGlobals()

const settings                  = inject("$settings")

const router                    = useRouter();
const route                     = useRoute();

const loadingOverlayStore       = useLoadingOverlayStore()
const { overlayVisibility }     = storeToRefs(loadingOverlayStore)

const { 
    flashMessages, 
    setMessageDuration 
}     = useFlashMessagesStore()

const adminAuthStore        = useAdminAuthStore()

const adminLayoutKey = computed(() => String(route.params.tenantId || 'guest'))
const isLayoutlessRoute = computed(() => route.name === 'login' || route.name === 'auth.callback' || route.meta?.public === true || route.meta?.layoutless === true)

setMessageDuration(1000)

router.beforeEach((to, from, next) => {

    document.querySelectorAll('.uk-modal').forEach((el) => {
        if (el.id !== 'modal-menu') {
            el.remove()
        }
    })

    if (!VueCookies.get('admin_token') && !['login', 'auth.callback'].includes(to.name) && to.meta?.public !== true) {
        next({ name: 'login', query: { redirect: to.fullPath } })
        return
    }

    if (requiresTrialUpgrade(to) && to.name !== 'admin.trial-expired' && to.meta?.public !== true) {
        next({ name: 'admin.trial-expired', params: { tenantId: resolveTenantId(to) } })
        return
    }

    if (adminAuthStore.admin && !requiresTrialUpgrade(to) && to.name === 'admin.trial-expired') {
        next(defaultAuthenticatedRouteFor(to))
        return
    }

    if (requiresSetup() && !isSetupRoute(to) && to.meta?.public !== true) {
        next(setupRouteFor(to))
        return
    }

    if (!requiresSetup() && isSetupRoute(to)) {
        next(defaultAuthenticatedRouteFor(to))
        return
    }

    next()
})

router.afterEach(() => {

})

const { mutate: askLogout } = useMutation(gql`
    mutation logout{
        adminLogout
    }
`)


const logout = async () => {

    const result        = await askLogout()

    if(result?.data?.adminLogout){

        adminAuthStore.clearAdmin()

        VueCookies.remove('admin_token')

        await router.push({ name: "login" })
    }

    console.log(result)
}

onMounted(async () => {

    if(!VueCookies.get('admin_token') && !['login', 'auth.callback'].includes(route.name) && route.meta?.public !== true) {
        await router.push({ name: 'login', query: { redirect: route.fullPath } })
        return
    }

    if (requiresTrialUpgrade(route) && route.name !== 'admin.trial-expired' && route.meta?.public !== true && route.meta?.allowWhenUpgradeRequired !== true) {
        await router.replace({ name: 'admin.trial-expired', params: { tenantId: resolveTenantId(route) } })
        return
    }

    if (adminAuthStore.admin && !requiresTrialUpgrade(route) && route.name === 'admin.trial-expired') {
        await router.replace(defaultAuthenticatedRouteFor(route))
        return
    }

    if (requiresSetup() && !isSetupRoute(route) && route.meta?.public !== true) {
        await router.replace(setupRouteFor(route))
    }
})

function requiresSetup() {
    return !!VueCookies.get('admin_token') && adminAuthStore.admin?.setup_completed === false
}

function requiresTrialUpgrade(targetRoute) {
    const tenant = tenantForRoute(targetRoute)

    return !!VueCookies.get('admin_token') && tenant?.requires_upgrade === true
}

function tenantForRoute(targetRoute) {
    const tenants = Array.isArray(adminAuthStore.admin?.tenants)
        ? adminAuthStore.admin.tenants
        : []

    const tenantId = targetRoute.params?.tenantId || route.params?.tenantId

    if (!tenantId) {
        return null
    }

    return tenants.find((tenant) => tenant?.endpoint === tenantId) || null
}

function resolveTenantId(targetRoute) {
    return targetRoute.params?.tenantId || route.params?.tenantId || null
}

function isSetupRoute(targetRoute) {
    return ['admin.setup', 'admin.setup.global'].includes(targetRoute.name)
}

function setupRouteFor(targetRoute) {
    const tenantId = resolveTenantId(targetRoute)

    return tenantId
        ? { name: 'admin.setup', params: { tenantId } }
        : { name: 'admin.setup.global' }
}

function defaultAuthenticatedRouteFor(targetRoute) {
    const tenantId = resolveTenantId(targetRoute)

    return tenantId
        ? { name: 'dashboard.index', params: { tenantId } }
        : { name: 'workspaces.global' }
}
</script>
